import { formatCents, formatCount, formatPercent, tt, formatDecimal } from '@agos/shared'

import { slotsChanged, slotsPayload, type SlotForm } from './mini-game-slots'

/**
 * The campaigns tab of `/loyalty-program` — fifteen campaign types, two mini-games, the wheel, the
 * leaderboard and the referral rules.
 *
 * **FB-166.** The catalogue holds **fifteen** types and legacy renders **thirteen**. `CAMPAIGN_GROUPS` covers
 * twelve across five groups and `rebricek` is rendered on its own; **`push_blast` is on no screen** — while
 * legacy puts it in `MANUAL_RUNNABLE` *and* `NOTIFICATION_CAMPAIGN_TYPES`, declaring it runnable and
 * notification-bearing with no card to run it from.
 *
 * `spin_wheel` is also absent from the cards, and that is **right**. `CampaignCatalog`'s docblock:
 *
 * > `spin_wheel` is in the enum and has no defaults: the wheel keeps its configuration in
 * > `wheel_campaign_settings` rather than in the campaign row, so the row exists only to give the wheel a
 * > place in the list and a sent counter.
 *
 * Nothing in the backend reads that row's `is_active`. One unreachable campaign, not two — and the port says
 * which is which, because a screen that hides a working campaign and a screen that hides an inert row are
 * different mistakes.
 *
 * **FB-167.** Every campaign reports `redeemedCount: 0`, and it is structural. 1 657 coupons sent across the
 * three active campaigns and not one redemption — because no `UPDATE` of `redeemed_count` exists in either
 * codebase. This is FB-113 seen at its source: `/analytics` reports the rate, and this is the screen that
 * feeds it. Legacy prints the zero beside the sent count as though it were a measurement.
 *
 * **FB-168.** A refusal is recorded as a run. `cashback`'s newest `campaign_runs` row is stamped 29 July with
 * `recipientCount: 0` and the error *"Tento typ kampane sa nedá spustiť manuálne (spúšťa sa automaticky po
 * objednávke)"*. Legacy's card shows *last run* from the same row, so a campaign that refused to run reads as
 * one that ran and reached nobody — which is also what `meninova_zlava` genuinely did on 28 July.
 */

// ─── The catalogue ───────────────────────────────────────────────────────────

/** Every value of the `campaign_type` enum, in the order Postgres declares it. */
export const CAMPAIGN_TYPES: readonly string[] = [
  'meninova_zlava',
  'kuponove_tornado',
  'kupon_za_vernost',
  'reaktivacia_neaktivnych',
  'cashback',
  'happy_hours',
  'personalizovana_ponuka',
  'rebricek',
  'click_and_win',
  'scratch_card',
  'spin_wheel',
  'push_blast',
  'black_friday',
  'welcome_first_order',
  'free_delivery_weekend',
  // §9.781, BACKEND.md §10A.435 - points to the customers chosen, sent only by hand.
  'bonusove_body',
]

/** The two the wheel and the games own outright — their settings live elsewhere. */
export const MINI_GAME_TYPES: readonly string[] = ['click_and_win', 'scratch_card']

/**
 * The campaign row `spin_wheel` keeps only so the wheel has a place in the list.
 *
 * Its `isActive` is inert by design and nothing reads it, so the card list leaves it out — as legacy does —
 * and the wheel panel carries the real switch.
 */
export const WHEEL_PLACEHOLDER_TYPE = 'spin_wheel'

/**
 * The customer cashback, which the campaigns table no longer draws as a campaign - `FRONTEND.md` §9.785,
 * owner O138g.
 *
 * The owner: *"My nepotrebujeme cashback pre zákazníka, pretože už cashback robíme už formou vernostných
 * bodov"*. So the *Cashback* row in *Vernostné odmeny* is the organization cashback (§9.782) and this card -
 * a percentage of an online-paid order back as a coupon (§10A.105), and its send by hand (§10A.434) - is
 * not offered. **It stays in its group**, so `unreachableTypes` still holds and a row the server sends is
 * not drawn as a campaign this screen does not know; it is left out by `groupedCampaigns` instead, and a
 * franchise that still has it switched on is told so on the organization row (`retiredCashbackStillOn`).
 */
export const RETIRED_CASHBACK_TYPE = 'cashback'

/** The group the organization cashback row is drawn in, where *Cashback* was. */
export const ORGANIZATION_CASHBACK_GROUP = 'rewards'

/**
 * The customer cashback rows still switched on - the one state its retired card must not hide.
 *
 * Every such row, not only the first of the type: §10A.2 lets a type have several, and *Vypnúť* switches each
 * off by its id, because `PATCH /campaigns/{type}` reaches only the primary one.
 */
export function retiredCashbackStillOn(campaigns: readonly Campaign[]): Campaign[] {
  return campaigns.filter((campaign) => campaign.type === RETIRED_CASHBACK_TYPE && campaign.isActive)
}

/** The types *Ďalšia kampaň toho istého typu* offers - not the retired one, which nothing would draw. */
export function creatableTypes(): string[] {
  return CAMPAIGN_TYPES.filter((type) => type !== RETIRED_CASHBACK_TYPE)
}

export interface CampaignGroup {
  key: string
  types: readonly string[]
}

export const CAMPAIGN_GROUPS: readonly CampaignGroup[] = [
  {
    key: 'rewards',
    types: ['kupon_za_vernost', 'cashback', 'meninova_zlava', 'welcome_first_order'],
  },
  { key: 'reactivation', types: ['reaktivacia_neaktivnych'] },
  { key: 'blast', types: ['kuponove_tornado', 'personalizovana_ponuka', 'push_blast', 'bonusove_body'] },
  { key: 'promo', types: ['happy_hours', 'black_friday', 'free_delivery_weekend'] },
  { key: 'gamification', types: ['click_and_win', 'scratch_card'] },
  { key: 'ranking', types: ['rebricek'] },
]

export function groupTitle(key: string): string {
  if (key === 'rewards') return tt('Vernostné odmeny')
  if (key === 'reactivation') return tt('Reaktivácia')
  if (key === 'blast') return tt('Hromadné akcie')
  if (key === 'promo') return tt('Časové akcie')
  if (key === 'gamification') return tt('Hry pre zákazníkov')
  return tt('Rebríček')
}

export function groupDescription(key: string): string {
  if (key === 'rewards') return tt('Odmena po splnení podmienky - objednávka, meniny, prvý nákup.')
  if (key === 'reactivation') return tt('Oslovenie zákazníkov, ktorí dlhšie neobjednávali.')
  if (key === 'blast') return tt('Rozposlanie zákazníkom - kupóny, ponuky, body, push.')
  if (key === 'promo')
    // §9.778 - the cart applies the discount; an announcement goes only when somebody sends one.
    return tt('Akcie viazané na čas. Zľavu aplikuje košík, oznámenie pošlete tlačidlom Poslať teraz.')
  if (key === 'gamification')
    return tt('Hru spúšťa zákazník sám. Kampaň len rozhoduje, či je dostupná.')
  return tt('Mesačný rebríček zákazníkov podľa tržby.')
}

/**
 * Which types the card list actually draws.
 *
 * The wheel's placeholder row is excluded and everything else in the enum is grouped — so a type added to the
 * backend appears somewhere instead of disappearing, which is FB-166's whole point.
 */
export function renderedTypes(): string[] {
  return CAMPAIGN_GROUPS.flatMap((group) => group.types)
}

/** A type in the enum that no group claims. Empty by construction; asserted, so it stays empty. */
export function unreachableTypes(): string[] {
  const rendered = new Set([...renderedTypes(), WHEEL_PLACEHOLDER_TYPE])
  return CAMPAIGN_TYPES.filter((type) => !rendered.has(type))
}

/** The types legacy leaves out, kept so a test can show the difference rather than describe it. */
export const LEGACY_UNRENDERED: readonly string[] = ['push_blast', 'spin_wheel']

export function campaignName(type: string): string {
  if (type === 'meninova_zlava') return tt('Meninová zľava')
  if (type === 'kuponove_tornado') return tt('Kupónové tornádo')
  if (type === 'kupon_za_vernost') return tt('Kupón za vernosť')
  if (type === 'reaktivacia_neaktivnych') return tt('Reaktivácia neaktívnych')
  if (type === 'cashback') return tt('Cashback')
  if (type === 'happy_hours') return tt('Happy hours')
  if (type === 'personalizovana_ponuka') return tt('Personalizovaná ponuka')
  if (type === 'rebricek') return tt('Rebríček')
  if (type === 'click_and_win') return tt('Klikni a vyhraj')
  if (type === 'scratch_card') return tt('Stieracia karta')
  if (type === 'spin_wheel') return tt('Koleso šťastia')
  if (type === 'push_blast') return tt('Push rozposlanie')
  if (type === 'black_friday') return tt('Black Friday')
  if (type === 'welcome_first_order') return tt('Vitajte - prvá objednávka')
  if (type === 'free_delivery_weekend') return tt('Doprava zadarmo')
  if (type === 'bonusove_body') return tt('Bonusové body')
  // A type the backend adds and this table has not learned yet shows its key rather than nothing.
  return type
}

export function isNamedCampaign(type: string): boolean {
  return campaignName(type) !== type
}

/**
 * How a campaign starts. Only these three can be run by hand as they always could.
 *
 * §9.778 - `push_blast` left this list: *Poslať teraz* sends it by the card's channels to a chosen audience,
 * where *Spustiť teraz* sent push alone, so two buttons on one card would send two different things.
 */
export const MANUAL_RUNNABLE: readonly string[] = ['meninova_zlava', 'reaktivacia_neaktivnych', 'rebricek']

export type TriggerKind = 'schedule' | 'order' | 'checkout' | 'customer' | 'manual' | 'configured'

/**
 * Which campaign fires how - `FRONTEND.md §9.434`.
 *
 * **Every type is named, and there is no default branch**, which is the whole of §9.434. This used to
 * end `return 'schedule'`, and `kuponove_tornado` matched none of the branches - so its card said
 * *"Beží automaticky každý deň o 15:00. Ručne sa dá spustiť aj teraz."* while it fires on a card
 * payment and `mayRunByHand` refuses it. The owner read that beside *Nikdy nebežala* and reported the
 * campaign as broken, which is the only conclusion that message allows.
 *
 * Five of fifteen types reached that default and it was right for three. **A fall-through that is
 * correct most of the time is the worst kind**, because it looks maintained - so an unlisted type is
 * now a test failure (`campaign-triggers.test.ts`) rather than a confident sentence about 15:00.
 *
 * Worth stating per card, because legacy's *Spustiť teraz* button appears on cards that refuse it and the
 * refusal only arrives after the click - see FB-168.
 */
export function triggerOf(type: string): TriggerKind {
  if (type === 'kupon_za_vernost' || type === 'cashback' || type === 'welcome_first_order')
    return 'order'
  if (type === 'happy_hours' || type === 'black_friday' || type === 'free_delivery_weekend')
    return 'checkout'
  if (MINI_GAME_TYPES.includes(type) || type === WHEEL_PLACEHOLDER_TYPE) return 'customer'
  if (type === 'push_blast' || type === 'bonusove_body') return 'manual'
  // §9.434 - it chooses its own event, so the card has to read the setting rather than the type.
  if (type === 'kuponove_tornado') return 'configured'
  // `PersonalisedOffer` writes a `campaign_runs` row from its own pass, so this one is a run.
  if (type === 'personalizovana_ponuka') return 'schedule'
  if (type === 'meninova_zlava' || type === 'reaktivacia_neaktivnych' || type === 'rebricek') {
    return 'schedule'
  }
  // Not a default: an unknown type has no honest sentence, and saying so is better than saying 15:00.
  return 'configured'
}

/** The three events `kuponove_tornado` can be set to - `BACKEND.md §10A.199`. */
export const TORNADO_TRIGGERS: readonly string[] = ['card_online', 'web_order', 'any_order']

/** What the campaign's own `trigger` setting is, defaulting as the server defaults. */
export function tornadoTrigger(settings: Record<string, unknown> | null | undefined): string {
  const asked = settings?.trigger
  return typeof asked === 'string' && TORNADO_TRIGGERS.includes(asked) ? asked : 'card_online'
}

/** What each choice means, in the owner's own terms rather than in slugs. */
export function tornadoTriggerLabel(trigger: string): string {
  if (trigger === 'web_order') return tt('Po objednávke z webu (aj pri platbe v hotovosti)')
  if (trigger === 'any_order') return tt('Po každej objednávke, aj telefonickej')
  return tt('Po online platbe kartou')
}

export function triggerMessage(trigger: TriggerKind): string {
  // §9.780 - not *Ručne nie* any more: Vernostný kupón and Cashback are sent by hand too (#191), and their
  // *Poslať teraz* says how; `welcome_first_order` says it has no sender on its own line.
  if (trigger === 'order') return tt('Spustí sa po objednávke, ktorá splní podmienku.')
  // §9.778 - true until somebody presses *Poslať teraz*, so it says so.
  if (trigger === 'checkout')
    return tt('Zľavu aplikuje košík. Zákazníkom ju oznámite tlačidlom Poslať teraz.')
  if (trigger === 'customer')
    return tt('Spustí ju zákazník sám. Kampaň len rozhoduje, či je dostupná.')
  if (trigger === 'manual') return tt('Beží len ručne - sama sa nespustí nikdy.')
  if (trigger === 'configured') return tt('Spúšťa sa podľa nastavenia nižšie. Ručne nie.')
  return tt('Beží automaticky každý deň o 15:00. Ručne sa dá spustiť aj teraz.')
}

/**
 * The sentence on a tornado card, which names the event it is actually waiting for - §9.434.
 *
 * The owner's report was a card claiming a 15:00 schedule beside *Nikdy nebežala*. This is what it
 * says instead: the event, in the words the nástenka uses for the same orders.
 */
export function tornadoTriggerMessage(
  settings: Record<string, unknown> | null | undefined,
): string {
  const chosen = tornadoTrigger(settings)
  if (chosen === 'web_order') {
    return tt('Spustí sa po každej objednávke z webu - tých, čo majú na nástenke odznak Web.')
  }
  if (chosen === 'any_order') {
    return tt('Spustí sa po každej objednávke, vrátane telefonických.')
  }
  return tt('Spustí sa len po úspešnej online platbe kartou. Pri platbe v hotovosti nikdy.')
}

export function mayRunByHand(type: string): boolean {
  return MANUAL_RUNNABLE.includes(type)
}

// ─── The rows ────────────────────────────────────────────────────────────────

export interface CampaignRun {
  ranAt: string
  recipientCount: number
  successCount: number
  failedCount: number
  errorMessage: string | null
  /**
   * `ran` | `refused`, or `null` on a row written before `campaign_runs.status` existed — FB-168.
   *
   * `null` is not a default to fall back into quietly: those rows genuinely do not carry the fact, and
   * `runOutcome` infers for them the way it always did. Rows written since state it.
   */
  status: string | null
}

export interface Campaign {
  id: number
  type: string
  /** §10A.2's own name for this row. Null on all fifteen legacy rows; set when a type has several. */
  name: string | null
  isActive: boolean
  settings: Record<string, unknown>
  segment: Record<string, unknown>
  startsAt: string | null
  endsAt: string | null
  sentCount: number
  /** §10A.99's stale column: written at insert, never incremented. Declared, never counted from. */
  redeemedCount: number
  /**
   * How many of this campaign's coupons carry a recorded origin - §10A.310, FB-900.
   *
   * **Nought means unmeasured, not nought redeemed**, and that is the whole field. `redeemedCount` is
   * never incremented, so a rate from it is not a measurement; §10A.98 gave a coupon an origin, which
   * makes the real count a join. A campaign whose coupons predate that has nothing to join to.
   *
   * **`null` when the server does not answer it** - a backend older than §10A.310 - and absent is
   * treated as unmeasured, which is the safe direction: it keeps the screen saying *nemerané* rather
   * than inventing a rate from a column nobody writes.
   */
  trackedCoupons: number | null
  /** Redemptions counted from the orders through the coupon's origin - §10A.99, §10A.310. */
  redeemedActual: number | null
  lastRun: CampaignRun | null
  /**
   * Whether anything in AGOS can send this type - §10A.279, owner #77.
   *
   * **`null` when the server does not answer it**, which is a backend older than that entry. Absent is not
   * false: announcing *this can never send* from a missing field would be §9.532's defect with the sides
   * swapped, and `runMessage` acts on an explicit `false` only.
   */
  hasSender: boolean | null
  /**
   * What the campaign says to a customer and how - §9.777, BACKEND.md §10A.431. `null` for a campaign
   * that sends no customer message, and from a server older than that entry, which drew the channel
   * ticks on every campaign and honoured them on one.
   */
  messages: CampaignMessages | null
}

/** §9.777 - a campaign's message, as `GET /campaigns` answers it. */
export interface CampaignMessages {
  /** The channels this campaign's sender can use, in the order they are tried. */
  channels: MessageChannel[]
  mode: ChannelMode
  /** What would be sent where the card stores no text of its own, keyed as `MESSAGE_TEXT_KEYS`. */
  defaults: Partial<Record<MessageTextKey, string>>
  /** The placeholders the campaign fills, without their braces. */
  placeholders: string[]
  /**
   * §9.780, BACKEND.md §10A.434 - what a send by hand would say where the card stores no `manual*` text,
   * keyed as `defaults`. Empty for a campaign not sent by hand as a coupon, and from an older server.
   */
  byHandDefaults: Partial<Record<MessageTextKey, string>>
  /** The placeholders a send by hand fills. */
  byHandPlaceholders: string[]
}

export type MessageChannel = 'push' | 'email' | 'sms'
export type ChannelMode = 'fallback' | 'all'

/** The order the owner asked for: push, then e-mail, then SMS - BACKEND.md §10A.431. */
export const MESSAGE_CHANNEL_ORDER: readonly MessageChannel[] = ['push', 'email', 'sms']

/** The six texts a campaign carries, in the order the card draws them. `CampaignTexts.KEYS`. */
export const MESSAGE_TEXT_KEYS = [
  'pushTitle',
  'pushBody',
  'emailSubject',
  'emailHeadline',
  'emailMessage',
  'smsText',
] as const

export type MessageTextKey = (typeof MESSAGE_TEXT_KEYS)[number]

/**
 * §9.780 - the six texts of a send by hand, in `MESSAGE_TEXT_KEYS`' order. `CampaignTexts.MANUAL_KEYS`, held
 * together by `CampaignMessageKeysAgreeTest`.
 */
export const BY_HAND_TEXT_KEYS = [
  'manualPushTitle',
  'manualPushBody',
  'manualEmailSubject',
  'manualEmailHeadline',
  'manualEmailMessage',
  'manualSmsText',
] as const

export type ByHandTextKey = (typeof BY_HAND_TEXT_KEYS)[number]

/** `pushTitle` as `manualPushTitle` - the key a send by hand's text is stored under. */
export function byHandKeyOf(key: MessageTextKey): ByHandTextKey {
  return BY_HAND_TEXT_KEYS[MESSAGE_TEXT_KEYS.indexOf(key)] ?? 'manualPushTitle'
}

function textsOf(value: unknown): Partial<Record<MessageTextKey, string>> {
  const stored =
    typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
  const texts: Partial<Record<MessageTextKey, string>> = {}
  for (const key of MESSAGE_TEXT_KEYS) {
    const text = stored[key]
    if (typeof text === 'string') texts[key] = text
  }
  return texts
}

function namesOf(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : []
}

function messagesOf(value: unknown): CampaignMessages | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null
  const row = value as Record<string, unknown>
  const channels = Array.isArray(row.channels)
    ? MESSAGE_CHANNEL_ORDER.filter((channel) => (row.channels as unknown[]).includes(channel))
    : []
  return {
    channels,
    mode: row.mode === 'all' ? 'all' : 'fallback',
    defaults: textsOf(row.defaults),
    placeholders: namesOf(row.placeholders),
    byHandDefaults: textsOf(row.byHandDefaults),
    byHandPlaceholders: namesOf(row.byHandPlaceholders),
  }
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0
}

export function campaignsOf(payload: unknown): Campaign[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    const type = text(row.type)
    if (type === null) return []
    const run = row.lastRun as Record<string, unknown> | null | undefined
    return [
      {
        id: typeof row.id === 'number' ? row.id : 0,
        type,
        name: text(row.name),
        isActive: row.isActive === true,
        settings: (row.settings ?? {}) as Record<string, unknown>,
        segment: (row.segment ?? {}) as Record<string, unknown>,
        startsAt: text(row.startsAt),
        endsAt: text(row.endsAt),
        sentCount: count(row.sentCount),
        redeemedCount: count(row.redeemedCount),
        /* §10A.310 - kept as `null` rather than coerced to nought, because a server that does not
         * answer these has measured nothing and *absent* must not read as *counted and found none*.
         * `trackedCoupons` treats null as nought on purpose; the distinction matters only here. */
        trackedCoupons: typeof row.trackedCoupons === 'number' ? row.trackedCoupons : null,
        redeemedActual: typeof row.redeemedActual === 'number' ? row.redeemedActual : null,
        hasSender: typeof row.hasSender === 'boolean' ? row.hasSender : null,
        messages: messagesOf(row.messages),
        lastRun:
          run === null || run === undefined || text(run.ranAt) === null
            ? null
            : {
                ranAt: text(run.ranAt) ?? '',
                recipientCount: count(run.recipientCount),
                successCount: count(run.successCount),
                failedCount: count(run.failedCount),
                errorMessage: text(run.errorMessage),
                status: text(run.status),
              },
      },
    ]
  })
}

/**
 * The rows to draw, grouped, with the wheel's placeholder left out.
 *
 * Keyed by type into a **list**, not a single row. §10A.2 lets one type have several campaigns —
 * `MarketingController`'s own comment says a type used to name exactly one and no longer does — so a lookup
 * that kept one row per type would draw the last and drop the rest without saying so. On this data every type
 * has exactly one, which is precisely when that bug would go unnoticed.
 */
export function groupedCampaigns(
  campaigns: readonly Campaign[],
): { key: string; campaigns: Campaign[] }[] {
  const byType = new Map<string, Campaign[]>()
  for (const campaign of campaigns) {
    // §9.785 - the organization cashback row stands where this one was.
    if (campaign.type === RETIRED_CASHBACK_TYPE) continue
    const existing = byType.get(campaign.type)
    if (existing === undefined) byType.set(campaign.type, [campaign])
    else existing.push(campaign)
  }
  return CAMPAIGN_GROUPS.map((group) => ({
    key: group.key,
    campaigns: group.types.flatMap((type) => byType.get(type) ?? []),
  })).filter((group) => group.campaigns.length > 0 || group.key === ORGANIZATION_CASHBACK_GROUP)
}

/** The types the server sent more than one campaign for — each needs its own name to be told apart. */
export function typesWithSeveral(campaigns: readonly Campaign[]): string[] {
  const counts = new Map<string, number>()
  for (const campaign of campaigns) {
    // §9.785 - a type the table does not draw cannot be one whose rows it shows *all* of.
    if (campaign.type === RETIRED_CASHBACK_TYPE) continue
    counts.set(campaign.type, (counts.get(campaign.type) ?? 0) + 1)
  }
  return [...counts.entries()].filter(([, howMany]) => howMany > 1).map(([type]) => type)
}

/**
 * What to call one row.
 *
 * The catalogue name unless the row carries its own — which is how §10A.2's second campaign of a type is told
 * from the first. All fifteen rows on this data have `name: null`, so the catalogue answers for all of them.
 */
export function campaignLabel(campaign: Campaign): string {
  return campaign.name ?? campaignName(campaign.type)
}

/** A campaign's own window, if §10A.2 gave it one. Null on every legacy row. */
export function campaignWindow(
  campaign: Campaign,
): { from: string | null; to: string | null } | null {
  if (campaign.startsAt === null && campaign.endsAt === null) return null
  return { from: campaign.startsAt, to: campaign.endsAt }
}

/**
 * The window as a pair of `datetime-local` values — §9.262, owner O9.
 *
 * The column is a `timestamptz` and the input wants `YYYY-MM-DDTHH:mm` with no zone, so the stamp is
 * cut rather than parsed: a `Date` round-trip would move the value by the browser's offset, and a
 * campaign that starts an hour late on the customer's screen is exactly the bug this edits away.
 */
export function windowFormOf(campaign: Campaign): CampaignWindowForm {
  return { startsAt: localStampOf(campaign.startsAt), endsAt: localStampOf(campaign.endsAt) }
}

export interface CampaignWindowForm {
  startsAt: string
  endsAt: string
}

/**
 * A stored instant as the wall-clock time on the manager's own screen.
 *
 * **Not a substring of the stamp, which is what this was first written as.** The column is a
 * `timestamptz` and the server answers it in UTC — `2026-11-27T00:00Z`. Cutting the characters out
 * would put *00:00* in the box for a campaign that starts at **01:00** in Šurany, and the same
 * screen's read-only line above it prints `formatDateTime(startsAt)`, which is local. One screen,
 * two clocks, no warning.
 */
function localStampOf(stored: string | null): string {
  if (stored === null) return ''
  const instant = Date.parse(stored.trim())
  if (!Number.isFinite(instant)) return ''
  const local = new Date(instant)
  return (
    `${local.getFullYear()}-${pad2(local.getMonth() + 1)}-${pad2(local.getDate())}` +
    `T${pad2(local.getHours())}:${pad2(local.getMinutes())}`
  )
}

function pad2(value: number): string {
  return String(value).padStart(2, '0')
}

/**
 * A `datetime-local` value as an instant, with the offset that value means.
 *
 * **The offset has to be sent.** A zoneless stamp reaches `CAST(… AS timestamptz)` and PostgreSQL
 * resolves it against the *session* zone, which is `UTC` in this container — measured. So a Black
 * Friday set to begin at midnight began at 01:00, and at 02:00 in summer. Computed from the value
 * itself rather than from today, so a window set in November keeps CET while one set in July keeps
 * CEST.
 */
export function offsetStampOf(local: string): string {
  const typed = new Date(local)
  if (Number.isNaN(typed.getTime())) return local
  const minutes = -typed.getTimezoneOffset()
  const sign = minutes < 0 ? '-' : '+'
  const size = Math.abs(minutes)
  return `${local}:00${sign}${pad2(Math.floor(size / 60))}:${pad2(size % 60)}`
}

/**
 * What is wrong with the window somebody typed, in Slovak — the same two rules the creation form has,
 * because a window that ends before it starts is a campaign that never runs whichever screen set it.
 */
export function windowProblems(form: CampaignWindowForm): string[] {
  const found: string[] = []
  if (form.startsAt !== '' && !isStamp(form.startsAt)) found.push(tt('Neplatný dátum: začiatok'))
  if (form.endsAt !== '' && !isStamp(form.endsAt)) found.push(tt('Neplatný dátum: koniec'))
  if (
    form.startsAt !== '' &&
    form.endsAt !== '' &&
    isStamp(form.startsAt) &&
    isStamp(form.endsAt) &&
    form.endsAt.trim() <= form.startsAt.trim()
  ) {
    found.push(tt('Koniec musí byť po začiatku - inak kampaň nikdy nebeží'))
  }
  return found
}

/**
 * The body `PATCH /campaigns/id/{id}` takes for a window.
 *
 * **A cleared bound is `null`, and the key must be present to clear it** — the server's own comment
 * says so: *"A window bound is cleared by sending null, so containsKey is the test rather than the
 * value."* Omitting the key leaves the old bound in place, so a campaign somebody just un-ended would
 * still end.
 */
export function windowPayload(form: CampaignWindowForm): Record<string, unknown> {
  return {
    startsAt: form.startsAt.trim() === '' ? null : offsetStampOf(form.startsAt.trim()),
    endsAt: form.endsAt.trim() === '' ? null : offsetStampOf(form.endsAt.trim()),
  }
}

/** Whether the window on screen differs from the one stored. */
export function windowChanged(campaign: Campaign, form: CampaignWindowForm): boolean {
  const stored = windowFormOf(campaign)
  return stored.startsAt !== form.startsAt.trim() || stored.endsAt !== form.endsAt.trim()
}

/**
 * What a window means for a campaign that is switched on, said in Slovak.
 *
 * A campaign can be active and outside its window, and the row would otherwise read *aktívna* while
 * the checkout ignores it — `CampaignService.active()` filters on `withinWindow` before anything else.
 *
 * `nowMs` rather than a string, and the bounds are parsed rather than compared as text: the column is
 * a `timestamptz` and the browser is in Bratislava, so `'2026-11-25T00:00:00Z' < '2026-11-25T01:00'`
 * is a comparison of two different clocks that happens to look like one.
 */
export function windowNote(campaign: Campaign, nowMs: number): string {
  if (!campaign.isActive) return ''
  const from = campaign.startsAt === null ? null : Date.parse(campaign.startsAt)
  const to = campaign.endsAt === null ? null : Date.parse(campaign.endsAt)
  if (from !== null && Number.isFinite(from) && nowMs < from) {
    return tt('Zapnutá, ale ešte nezačala - zákazník ju zatiaľ nevidí.')
  }
  if (to !== null && Number.isFinite(to) && nowMs > to) {
    return tt('Zapnutá, ale už skončila - zákazník ju nevidí.')
  }
  return ''
}

/** Whether the row narrows its audience. `{}` on every legacy row — meaning everyone who qualifies. */
export function hasSegment(campaign: Campaign): boolean {
  return Object.keys(campaign.segment).length > 0
}

/** A row the server sent that no group claims — so a new enum value is visible rather than lost. */
export function ungroupedCampaigns(campaigns: readonly Campaign[]): Campaign[] {
  const claimed = new Set([...renderedTypes(), WHEEL_PLACEHOLDER_TYPE])
  return campaigns.filter((campaign) => !claimed.has(campaign.type))
}

export function activeCount(campaigns: readonly Campaign[]): number {
  return campaigns.filter((campaign) => campaign.isActive).length
}

export function totalSent(campaigns: readonly Campaign[]): number {
  return campaigns.reduce((sum, campaign) => sum + campaign.sentCount, 0)
}

// ─── FB-167: the redemption rate that cannot exist ───────────────────────────

/**
 * The redemption rate, or `null` because it is not measured.
 *
 * `redeemed_count` had no `UPDATE` in either codebase, so the figure was structurally zero. A rate of
 * 0 % would be a measurement; *nemerané* is the truth. Same rule as FB-113 on `/analytics`, which reads
 * this column through a different endpoint.
 *
 * **AGOS writes it now, and this said otherwise until 2026-08-05.** `WebOrderDiscounts`
 * `.recordCampaignRedemption` runs `UPDATE campaigns SET redeemed_count = redeemed_count + 1`,
 * attributing to the first send of the coupon code. So the reason has moved from *structurally
 * impossible* to *nothing has run yet*: measured 2026-08-05, **15 campaigns, 1 655 sent, 0 redeemed,
 * and `campaign_sends` is empty** because no campaign has run since the migration.
 *
 * The branch below needs no change and is the reason this is a comment fix rather than a defect: it
 * tests `redeemed === 0` against the data, not against a belief about the column, so the real rate
 * appears by itself on the first redemption. A version that had hard-coded *nemerané* on the strength
 * of the sentence above would now be hiding a figure it could show.
 */
/**
 * How many coupons of these campaigns can be traced to an order at all - §10A.310.
 *
 * **This is the measurability test, and it is `manager-reporting`'s `redemptionIsUnmeasured` exactly.**
 * That rule has gated on the origin join since §10A.99 and this file gated on *is the redeemed total
 * nought* - so when one campaign's stale `redeemedCount` moved from 0 to 1, the analytics tab said
 * *nemerané* and this screen printed **`Uplatnených 0,0 %`**, the figure FB-167 exists to prevent.
 *
 * `null` counts as nought, because a server that does not answer the field cannot have measured
 * anything - which keeps the honest sentence rather than manufacturing a rate.
 */
export function trackedCoupons(campaigns: readonly Campaign[]): number {
  return campaigns.reduce((sum, campaign) => sum + (campaign.trackedCoupons ?? 0), 0)
}

/** Redemptions counted from the orders - §10A.99's figure, never `redeemedCount`. */
export function redeemedActual(campaigns: readonly Campaign[]): number {
  return campaigns.reduce((sum, campaign) => sum + (campaign.redeemedActual ?? 0), 0)
}

export function redemptionRate(campaigns: readonly Campaign[]): number | null {
  const sent = totalSent(campaigns)
  if (sent === 0) return null
  // §10A.310 - nought tracked is *nobody was counting*, which is not a rate of nought. A campaign
  // whose coupons predate §10A.98 has nothing to join to, so 0 % there would be a false claim.
  if (trackedCoupons(campaigns) === 0) return null
  return (redeemedActual(campaigns) / sent) * 100
}

export function redemptionLabel(campaigns: readonly Campaign[]): string {
  const rate = redemptionRate(campaigns)
  return rate === null ? tt('nemerané') : formatPercent(rate, 1)
}

export function redemptionNote(campaigns: readonly Campaign[]): string {
  if (redemptionRate(campaigns) !== null) return ''
  // The sends are real; the redemptions quoted are the ones that can be traced, which on data
  // predating §10A.98 is nought - and the sentence is what says why that nought is not an answer.
  return `${tt('Odoslaných kupónov')}: ${formatCount(totalSent(campaigns))}, ${tt('uplatnených')}: ${formatCount(redeemedActual(campaigns))}. ${tt('Uplatnenie sa nikde nezapisuje, takže nula neznamená, že ich nikto nepoužil - znamená, že sa to nemeria.')}`
}

// ─── FB-168: a refusal recorded as a run ─────────────────────────────────────

export type RunOutcome = 'never' | 'refused' | 'reached-nobody' | 'partial' | 'delivered'

/**
 * What the last run actually was.
 *
 * Legacy prints *last run* from the row and nothing else, so a refusal and a run that reached nobody read
 * identically — and both read as *it ran*.
 *
 * **`status` decides it when the row carries one** (FB-168). `campaign_runs.status` is written from the
 * moment the column existed; every earlier row is null and genuinely does not carry the fact, so those
 * fall back to the inference below — a message with nought recipients. Inferring over a row that states
 * its own outcome would be re-deriving something already known, and the two would eventually disagree.
 */
export function runOutcome(campaign: Campaign): RunOutcome {
  const run = campaign.lastRun
  if (run === null) return 'never'
  if (run.status === 'refused') return 'refused'
  if (run.status === 'ran' && run.recipientCount === 0) return 'reached-nobody'
  if (run.status === null && run.errorMessage !== null && run.recipientCount === 0) return 'refused'
  if (run.recipientCount === 0) return 'reached-nobody'
  return run.failedCount > 0 ? 'partial' : 'delivered'
}

export function runMessage(campaign: Campaign): string {
  const run = campaign.lastRun
  /* §10A.279, owner #77 - **in place of *never ran*, not beside it.** `welcome_first_order` has no sender in
     AGOS or legacy, and the owner has it switched on at Nesvady with nothing sent: *Nikdy nebežala* is true
     and reads as a fault waiting to clear, when **there is nothing to wait for**. That is the opposite choice
     from §9.533, where the backup job *can* run and its history is still worth reporting - the distinction is
     whether the fact is about this installation or about the feature.
     **Explicitly `false` only.** A server older than §10A.279 sends no field, and claiming a campaign can
     never send from an absent answer is the §9.532 defect mirrored. */
  if (campaign.hasSender === false) {
    return tt('Túto kampaň zatiaľ nič neodosiela - je zapnutá, ale sama sa nikdy nespustí.')
  }
  if (run === null) return tt('Nikdy nebežala.')
  const outcome = runOutcome(campaign)
  if (outcome === 'refused') {
    return `${tt('Posledný pokus server odmietol')}: ${run.errorMessage ?? ''}`
  }
  if (outcome === 'reached-nobody') {
    return tt('Posledný beh nikoho nenašiel - nikto nesplnil podmienku.')
  }
  if (outcome === 'partial') {
    return `${formatCount(run.successCount)} ${tt('doručených')}, ${formatCount(run.failedCount)} ${tt('zlyhalo')}.`
  }
  return `${formatCount(run.successCount)} ${tt('doručených')}.`
}

// ─── The daily send cap ──────────────────────────────────────────────────────

/**
 * How many campaign e-mails one person may get in a day, across every campaign.
 *
 * A §10A addition with no legacy screen. `CampaignRunner`'s own comment is the reason it matters: *"No
 * per-campaign cooldown can prevent three campaigns of three different types reaching the same person on one
 * morning — each of them is within its rights. This is the only guard that spans campaigns."*
 *
 * It is off on this data — `dailySendCap: 0`, `isActive: false` — and `overDailyCap` treats **both** as off:
 * `if (!capActive) return false` and then `if (cap <= 0) return false`. So switching the flag on while the
 * number is zero changes nothing at all, silently. The form refuses that combination.
 *
 * **`PATCH /campaigns/send-cap` refuses it too now** (FB-169), against the *resulting* state rather than the
 * body, so `{isActive: true}` alone with zero already stored is caught as well. That makes this check the
 * early word rather than the only one. `capState` keeps `'on-but-zero'` regardless: the refusal stops the
 * pair being *stored* from today, and says nothing about a row that reached it before.
 *
 * `updatedAt` is null until somebody first changes the cap — it used to report the migration's own insert,
 * so an untouched cap read as *last changed 1 August*.
 */
export interface SendCap {
  dailySendCap: number
  isActive: boolean
  updatedAt: string | null
}

export function sendCapOf(payload: unknown): SendCap {
  const row = (payload ?? {}) as Record<string, unknown>
  return {
    dailySendCap: count(row.dailySendCap),
    isActive: row.isActive === true,
    updatedAt: text(row.updatedAt),
  }
}

export type CapState = 'off' | 'on-but-zero' | 'on'

export function capState(cap: SendCap): CapState {
  if (!cap.isActive) return 'off'
  // The runner's second guard: an active cap of zero is not "nobody gets mail", it is no cap at all.
  return cap.dailySendCap <= 0 ? 'on-but-zero' : 'on'
}

export function capMessage(cap: SendCap): string {
  const state = capState(cap)
  if (state === 'off') {
    return tt(
      'Limit je vypnutý - jeden zákazník môže za jedno dopoludnie dostať e-mail z každej bežiacej kampane a nič tomu nezabráni.',
    )
  }
  if (state === 'on-but-zero') {
    return tt(
      'Limit je zapnutý, ale je nastavený na nulu - server ho v tom prípade ignoruje. Nula nie je zákaz.',
    )
  }
  return `${tt('Jeden zákazník dostane najviac')} ${formatCount(cap.dailySendCap)} ${tt('e-mailov denne zo všetkých kampaní spolu.')}`
}

/**
 * What the cap counts, which is not what the counters above show.
 *
 * `overDailyCap` counts today's rows in `campaign_sends`, and only this system writes that log — `recordSend`
 * is its single insert. The 1 657 sends the campaign cards report came from the old system, which had no such
 * table, so the log is empty and the two numbers will not agree for a while.
 *
 * Unconditional on purpose: no endpoint reports the size of `campaign_sends`, so a page that made the note
 * conditional would be deciding on a count it invented. The sentence is true either way.
 */
export function capHistoryNote(): string {
  return tt(
    'Limit počíta správy, ktoré odoslal tento systém. Počítadlá kampaní vyššie zahŕňajú aj odoslania zo starého systému, ktorý si ich jednotlivo nezapisoval - tie sa do limitu nerátajú.',
  )
}

export function capProblems(form: { dailySendCap: string; isActive: boolean }): string[] {
  const problems: string[] = []
  const raw = form.dailySendCap.trim()
  const parsed = Number(raw.replace(',', '.'))
  if (raw === '' || !Number.isFinite(parsed)) {
    problems.push(tt('Limit musí byť číslo.'))
  } else if (!Number.isInteger(parsed)) {
    problems.push(tt('Limit je celý počet správ.'))
  } else if (parsed < 0) {
    problems.push(tt('Limit nemôže byť záporný.'))
  } else if (form.isActive && parsed === 0) {
    problems.push(tt('Zapnutý limit musí byť aspoň 1 - nulu server ignoruje.'))
  }
  return problems
}

export function capPayload(form: { dailySendCap: string; isActive: boolean }): {
  dailySendCap: number
  isActive: boolean
} {
  return {
    dailySendCap: Math.round(Number(form.dailySendCap.trim().replace(',', '.'))),
    isActive: form.isActive,
  }
}

// ─── The wheel ───────────────────────────────────────────────────────────────

export interface WheelSegment {
  type: string
  label: string
  value: number
  weight: number
  color: string
}

export interface Wheel {
  isActive: boolean
  /** The basket a customer must reach before the wheel is offered at all. `5.00` on this data. */
  minOrderCents: number
  /** How long a win stays claimable. 72 hours on this data. */
  rewardValidityHours: number
  segments: WheelSegment[]
}

export function wheelOf(payload: unknown): Wheel | null {
  if (payload === null || payload === undefined || typeof payload !== 'object') return null
  const row = payload as Record<string, unknown>
  const segments = Array.isArray(row.segments) ? row.segments : []
  const minimum = row.minOrderAmount
  return {
    isActive: row.isActive === true,
    // A decimal string, like every other money field the API sends. §14.7 keeps it in cents.
    minOrderCents: Math.round(Number(typeof minimum === 'string' ? minimum : 0) * 100),
    rewardValidityHours: count(row.rewardValidityHours),
    segments: segments.map((entry) => {
      const segment = entry as Record<string, unknown>
      return {
        type: text(segment.type) ?? '',
        label: text(segment.label) ?? '',
        value: typeof segment.value === 'number' ? segment.value : 0,
        weight: count(segment.weight),
        color: text(segment.color) ?? '',
      }
    }),
  }
}

/**
 * The chance each segment really has.
 *
 * A weight is not a percentage. The eight live segments are weighted 30/15/25/30/15/5/15/5 = **140**, so the
 * first one — labelled *10 % zľava* — is won 21,4 % of the time and not 30 %. Legacy prints the raw weight,
 * which reads as a percentage and is wrong by half again on this very wheel.
 */
export function wheelOdds(wheel: Wheel): { segment: WheelSegment; percent: number }[] {
  const total = wheel.segments.reduce((sum, segment) => sum + segment.weight, 0)
  if (total === 0) return wheel.segments.map((segment) => ({ segment, percent: 0 }))
  return wheel.segments.map((segment) => ({ segment, percent: (segment.weight / total) * 100 }))
}

export function wheelWeightTotal(wheel: Wheel): number {
  return wheel.segments.reduce((sum, segment) => sum + segment.weight, 0)
}

export function wheelWeightsLookLikePercentages(wheel: Wheel): boolean {
  return wheelWeightTotal(wheel) === 100
}

/** The chance of winning nothing, which is the number nobody puts on a wheel and everybody should. */
export function wheelLoseChance(wheel: Wheel): number | null {
  const total = wheelWeightTotal(wheel)
  if (total === 0) return null
  const losing = wheel.segments
    .filter((segment) => segment.type === 'no_win' || segment.value === 0)
    .reduce((sum, segment) => sum + segment.weight, 0)
  return (losing / total) * 100
}

export function wheelNote(wheel: Wheel): string {
  const total = wheelWeightTotal(wheel)
  const base = `${tt('Váhy spolu')}: ${formatCount(total)} - ${tt('šanca je váha delená týmto súčtom, nie samotná váha.')}`
  // §9.35 — the one case where the rule above is invisible because it happens to be a no-op. At a
  // total of 100 every weight *is* its percentage, so a wheel reads correctly right up until somebody
  // adds a segment, at which point every chance on it changes and nothing on screen said it would.
  // This warning existed, was tested, and had no caller; the note is where it belongs, because a
  // second banner for a state one wheel in the country is in would be worse than a sentence.
  if (wheelWeightsLookLikePercentages(wheel)) {
    return `${base} ${tt('Súčet je práve 100, takže váhy sa teraz čítajú ako percentá - po pridaní segmentu to prestane platiť.')}`
  }
  if (!wheel.isActive) {
    return `${tt('Koleso je vypnuté, takže sa zákazníkovi nezobrazí.')} ${base}`
  }
  return base
}

/**
 * The two conditions on the wheel that are not on the wheel.
 *
 * `minOrderAmount` and `rewardValidityHours` come back from `GET /campaigns/wheel/admin` beside the segments,
 * and decide who is offered a spin and how long a prize lasts. Both are easy to leave off a screen that draws
 * the colourful part, and both change who wins anything.
 */
export function wheelConditions(wheel: Wheel): string {
  return `${tt('Koleso sa ponúkne od objednávky za')} ${formatCents(wheel.minOrderCents)}. ${tt('Výhru si zákazník musí uplatniť do')} ${formatCount(wheel.rewardValidityHours)} ${tt('hodín, inak prepadne.')}`
}

// ─── The leaderboard and the referrals ───────────────────────────────────────

export interface LeaderboardRow {
  rank: number
  customerId: number
  customerName: string
  totalSpentCents: number
  pointsBalance: number
}

export function leaderboardOf(payload: unknown): LeaderboardRow[] {
  const rows = (payload as { leaderboard?: unknown } | undefined)?.leaderboard
  if (!Array.isArray(rows)) return []
  return rows.map((entry) => {
    const row = entry as Record<string, unknown>
    const spent = row.totalSpent
    return {
      rank: count(row.rank),
      customerId: count(row.customerId),
      customerName: text(row.customerName) ?? tt('bez menoslova'),
      // The API sends a decimal string; §14.7 keeps money in cents.
      totalSpentCents: Math.round(Number(typeof spent === 'string' ? spent : 0) * 100),
      pointsBalance: count(row.pointsBalance),
    }
  })
}

/**
 * A past month's winners, as `GET /loyalty/leaderboard/history` answers them — §9.242, FB-402.
 *
 * The endpoint has been served since the port and read by nothing. It groups `leaderboard_snapshots`
 * by month, newest first, so the shape is a list of months each carrying its own ranked winners —
 * which is the shape legacy renders and the reason this is not just another `leaderboardOf`.
 *
 * **`couponCode` is the field the live board has no equivalent for**: it is what the winner was
 * actually given, and it is the only record that a month was ever awarded.
 */
export interface LeaderboardWinner {
  id: number
  rank: number
  customerName: string
  customerEmail: string | null
  totalSpentCents: number
  couponCode: string | null
}

export interface LeaderboardMonth {
  month: string
  winners: LeaderboardWinner[]
}

export function leaderboardHistoryOf(payload: unknown): LeaderboardMonth[] {
  const months = (payload as { history?: unknown } | undefined)?.history
  if (!Array.isArray(months)) return []
  return months.flatMap((entry) => {
    const row = entry as { month?: unknown; winners?: unknown }
    const month = text(row.month)
    if (month === null) return []
    const winners = Array.isArray(row.winners) ? row.winners : []
    return [
      {
        month,
        winners: winners.map((winner) => {
          const seat = winner as Record<string, unknown>
          const spent = seat.totalSpent
          return {
            id: count(seat.id),
            rank: count(seat.rank),
            customerName: text(seat.customerName) ?? tt('bez menoslova'),
            customerEmail: text(seat.customerEmail),
            // The API sends a decimal string, as the live board does — §14.7 keeps money in cents.
            totalSpentCents: Math.round(Number(typeof spent === 'string' ? spent : 0) * 100),
            couponCode: text(seat.couponCode),
          }
        }),
      },
    ]
  })
}

/**
 * The months a leaderboard may be asked about — §9.242.
 *
 * A leaderboard is a calendar-month total, so a month that has not happened can only be empty. The
 * bound is the running month; nothing stops a past month however old, because whether it holds
 * anything is the data's answer rather than the screen's.
 */
export function latestLeaderboardMonth(now: Date): string {
  return now.toISOString().slice(0, 7)
}

export function isLeaderboardMonth(value: string): boolean {
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(value)
}

export interface Referral {
  id: number
  status: string
  rewardPoints: number
  discountAppliedCents: number
  referrerName: string
}

export function referralsOf(payload: unknown): Referral[] {
  const rows = (payload as { referrals?: unknown } | undefined)?.referrals
  if (!Array.isArray(rows)) return []
  return rows.map((entry) => {
    const row = entry as Record<string, unknown>
    const discount = row.discountApplied
    return {
      id: count(row.id),
      status: text(row.status) ?? '',
      rewardPoints: count(row.rewardPoints),
      discountAppliedCents: Math.round(Number(typeof discount === 'string' ? discount : 0) * 100),
      referrerName: text(row.referrerName) ?? tt('bez menoslova'),
    }
  })
}

export function referralTotals(referrals: readonly Referral[]): {
  count: number
  points: number
  discountCents: number
  statuses: string[]
} {
  return {
    count: referrals.length,
    points: referrals.reduce((sum, referral) => sum + referral.rewardPoints, 0),
    discountCents: referrals.reduce((sum, referral) => sum + referral.discountAppliedCents, 0),
    statuses: [...new Set(referrals.map((referral) => referral.status))].sort(),
  }
}

/** Whether the status filter would have anything to filter. 17 of 17 are `rewarded` on this data. */
export function referralStatusesWorthFiltering(referrals: readonly Referral[]): boolean {
  return referralTotals(referrals).statuses.length > 1
}

// ─── §9.5's multi-campaign: making a second one of a type ────────────────────

/**
 * Creating, scheduling and removing campaigns — the last of §9.5's four backend upgrades.
 *
 * §10A.2 let a type hold several campaigns and gave them six endpoints under `/campaigns/id/{id}`, kept
 * deliberately separate from the by-type ones so *"nothing already written against /{type} has to change
 * to keep working"*. Measured: **15 campaigns of 15 types**, so no type has a second one and the whole
 * by-id half has never been reachable.
 *
 * **Three rules that are the server's, not this screen's.**
 *
 * 1. **A new campaign is created switched off.** `INSERT … is_active` is the literal `false`, whatever
 *    was sent. Right, and worth saying: a campaign that started sending the moment somebody typed a name
 *    would be a mail-out nobody reviewed.
 * 2. **The first campaign of a type cannot be deleted** — *"Základnú kampaň tohto typu nemožno zmazať —
 *    vypni ju namiesto toho"*. The list is meant to show every type, switched off if unused, and the
 *    `/{type}` endpoints have to keep resolving to something.
 * 3. **The window is validated, not stored blindly.** `boundary` refuses anything `Instants.parseOrNull`
 *    cannot read, per field. Measured: **0 of 15** campaigns carry a `starts_at` or an `ends_at`, so
 *    scheduling has never been used either.
 *
 * And the settings are not blank: `create` starts from `CampaignCatalog.defaultSettings(type)` and merges
 * what was sent over the top. So a campaign created here is a working one of that type, which is why this
 * form does not ask for settings at all — it asks for the three things that are genuinely per-campaign,
 * and the rest is edited where it already is.
 */

export interface NewCampaign {
  type: string
  name: string
  startsAt: string
  endsAt: string
}

export const EMPTY_NEW_CAMPAIGN: NewCampaign = { type: '', name: '', startsAt: '', endsAt: '' }

/** A `datetime-local` value, or a date, or nothing. Anything else the server would refuse by name. */
function isStamp(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2})?)?$/.test(value.trim())
}

export function newCampaignProblems(form: NewCampaign): string[] {
  const found: string[] = []
  if (!CAMPAIGN_TYPES.includes(form.type)) found.push(tt('Vyberte typ kampane'))
  if (form.name.trim() === '') {
    // Not the server's rule — its column is nullable — but a second campaign of a type that shares its
    // neighbour's name is indistinguishable in every list, including this one. FB-166 named that problem.
    found.push(tt('Pomenujte kampaň - bez názvu ju od tej vedľa nerozoznáte'))
  }
  if (form.startsAt !== '' && !isStamp(form.startsAt)) found.push(tt('Neplatný dátum: startsAt'))
  if (form.endsAt !== '' && !isStamp(form.endsAt)) found.push(tt('Neplatný dátum: endsAt'))
  if (
    form.startsAt !== '' &&
    form.endsAt !== '' &&
    isStamp(form.startsAt) &&
    isStamp(form.endsAt)
  ) {
    if (form.endsAt.trim() <= form.startsAt.trim()) {
      found.push(tt('Koniec musí byť po začiatku - inak kampaň nikdy nebeží'))
    }
  }
  return found
}

/** The body `POST /campaigns` takes. An empty window is `null`, not an empty string. */
export function newCampaignPayload(form: NewCampaign): Record<string, unknown> {
  return {
    type: form.type,
    name: form.name.trim(),
    // §9.262 — through `offsetStampOf` like the window editor's. Two builders writing the same two
    // columns with different meanings is how a campaign created here and one edited there disagree
    // about when they start.
    startsAt: form.startsAt.trim() === '' ? null : offsetStampOf(form.startsAt.trim()),
    endsAt: form.endsAt.trim() === '' ? null : offsetStampOf(form.endsAt.trim()),
  }
}

/** Said under the button, because a campaign that ran on creation would be a mail-out nobody reviewed. */
export function creationNote(): string {
  return tt(
    'Nová kampaň sa založí vypnutá - server ju vytvorí neaktívnu, nech si ju najprv prezriete. Nastavenia dostane predvolené pre svoj typ.',
  )
}

/**
 * Whether this campaign can be deleted at all.
 *
 * The lowest id of a type is its primary and the server refuses to remove it. That is not visible on the
 * row, so it is computed from the list the screen already has, and the button explains itself rather than
 * failing on click.
 */
export function mayDelete(campaign: Campaign, campaigns: readonly Campaign[]): boolean {
  const ofType = campaigns.filter((other) => other.type === campaign.type)
  if (ofType.length <= 1) return false
  const primary = ofType.reduce((lowest, other) => (other.id < lowest.id ? other : lowest))
  return primary.id !== campaign.id
}

export function deleteRefusalNote(): string {
  return tt(
    'Toto je základná kampaň svojho typu - zmazať sa nedá, lebo zoznam má ukázať každý typ a ostatné rozhrania sa naň odvolávajú. Vypnite ju namiesto toho.',
  )
}

// `perCampaignSends` was here and is deleted — §9.35, 2026-08-06. It gated the card's send figure on
// `campaign_sends` having rows, answering *nemerané* while that table is empty. The premise was
// wrong: the card shows `campaigns.sent_count`, a counter `CampaignRunner` and `PersonalisedOffer`
// increment directly, and it holds real history — 1 655 sends across six campaigns on this data,
// 616 for *reaktivácia neaktívnych* alone. Wiring the function would have hidden all of it behind
// "unmeasured". It was never called, so nothing was ever hidden; it is removed rather than left as a
// trap for whoever wires the list next.

/**
 * One editable field of a campaign's settings — §9.141, the owner's points 32 and 33.
 *
 * <p><b>Typed from the value, because that is the only description these fields have.</b> The
 * backend's catalogue holds defaults per type — `{cashbackPercent: 5, minOrderValue: 20}`,
 * `{startTime: '14:00', bonusPointsPercent: 30}` — and no schema beyond them. Inventing a richer one
 * would be asserting a contract the server does not offer.
 */
export type CampaignFieldKind = 'number' | 'time' | 'boolean' | 'text' | 'choice'

export interface CampaignField {
  key: string
  kind: CampaignFieldKind
  value: string | number | boolean
}

const TIME_KEY = /(^|[a-z])time$/i
const HH_MM = /^([01]?\d|2[0-3]):[0-5]\d$/

/**
 * What each campaign setting is called, in Slovak — §9.273, owner O13.
 *
 * *"in campaigns, would be good if you also replace placeholders.. like for example minOrderValue,
 * cashbackPercent etc."* The drawer rendered `{field.key}` as the label, so the editor read
 * `minOrderValue`, `cashbackPercent`, `couponValidityHours` — the raw camelCase keys of the stored
 * `settings` jsonb. Every one of the twenty-five keys the fifteen campaigns actually carry is named
 * here, read off the live rows rather than off the catalogue, so nothing that exists is missed.
 *
 * **An unknown key keeps its own name**, marked, rather than being guessed at: a settings key this
 * build has never seen is a new backend field, and silently inventing a Slovak name for it is how a
 * screen comes to describe something it does not understand.
 */
/* eslint-disable no-restricted-syntax -- These are the *sources* a translation is looked up by, not
   text rendered from here: `campaignFieldLabel` passes each through `tt()` at the point of display.
   Wrapping them at definition would freeze the lookup at module load, so a language change would
   leave the labels in Slovak. FRONTEND.md §8/§14.2 asks for the call, and the call is below. */
const FIELD_LABELS: Record<string, string> = {
  minOrderValue: 'Minimálna hodnota objednávky (€)',
  discountPercent: 'Zľava (%)',
  discountAmount: 'Zľava (€)',
  discountValue: 'Hodnota zľavy',
  discountType: 'Typ zľavy (percent / fixed)',
  cashbackPercent: 'Cashback (%)',
  bonusPointsPercent: 'Bonusové body navyše (%)',
  validityDays: 'Platnosť kupónu (dni)',
  expiryDays: 'Platnosť kupónu (dni)',
  couponValidityHours: 'Platnosť kupónu (hodiny)',
  couponPrefix: 'Predpona kódu kupónu',
  frequencyPerDay: 'Počet hier za deň',
  // Read by **nothing** on the server — `frequencyPerDay` beside it is the one `MiniGameService`
  // uses. §9.276 moved the warning out of the label and into `inertNote`, so all three dead settings
  // say it the same way.
  playsPerDay: 'Počet hier za deň',
  orderCount: 'Počet objednávok potrebných na kupón',
  inactiveMonths: 'Neaktívny aspoň (mesiace)',
  topN: 'Koľkí najlepší dostanú odmenu',
  targetSegment: 'Komu sa pošle (segment)',
  promoLabel: 'Názov akcie na účte',
  // §9.434, the owner's ask. A choice rather than free text - three events, and a fourth word
  // would silently narrow to the card on the server.
  trigger: 'Kedy sa kampaň spustí',
  stackableWithCoupons: 'Dá sa kombinovať s kupónom',
  daysOfWeek: 'Dni v týždni (1 = pondelok)',
  startTime: 'Začiatok',
  endTime: 'Koniec',
  pushTitle: 'Nadpis notifikácie',
  pushBody: 'Text notifikácie',
  pushUrl: 'Odkaz notifikácie',
  // §9.781 - what a send of *Bonusové body* adds to each customer.
  points: 'Počet bodov',
}

/* eslint-enable no-restricted-syntax */

export function campaignFieldLabel(key: string): string {
  const known = FIELD_LABELS[key]
  return known === undefined ? key : tt(known)
}

/** Whether this build knows what the key means — an unknown one is drawn as the developer name. */
export function isKnownField(key: string): boolean {
  return FIELD_LABELS[key] !== undefined
}

/**
 * Settings the server stores and never reads — §9.276, FB-434.
 *
 * **Keyed by `(type, key)`, because inertness is.** `minOrderValue` is read for `meninova_zlava`,
 * `kuponove_tornado`, `reaktivacia_neaktivnych` and `kupon_za_vernost`, and ignored by the two games,
 * whose coupon is written with `min_order_amount` hardcoded to `0` — **as legacy's is**
 * (`mini-game.ts:220`), so it is faithful rather than broken. A note keyed on the setting alone would
 * warn about four campaigns where it works.
 *
 * `''` for everything else, so the caller does not have to know which case it is in.
 */
export function inertNote(type: string, key: string): string {
  if (key === 'playsPerDay') {
    return tt('Server toto pole nečíta - počet hier riadi „Počet hier za deň“ nad ním.')
  }
  if (key === 'minOrderValue' && (type === 'click_and_win' || type === 'scratch_card')) {
    return tt('Server toto pole pri hrách nečíta - vyhraný kupón platí bez minimálnej sumy.')
  }
  // §9.780 - `cashbackPercent` is read: `OrderRewards` pays the share after an online payment (§10A.105) and a
  // send by hand takes it off the next order (§10A.434). The note that said cashback was not programmed was
  // written before §10A.105 and outlived it, on the card the owner sends cashback from.
  return ''
}

export function campaignFieldKind(key: string, value: unknown): CampaignFieldKind {
  if (typeof value === 'boolean') return 'boolean'
  if (typeof value === 'number') return 'number'
  // A time is a string that both looks like one and is named like one — `startTime: '14:00'`.
  // Either test alone is wrong: `name: '10:00'` is not a time, and `startTime: 'ráno'` is not one
  // a time input can hold.
  if (typeof value === 'string' && TIME_KEY.test(key) && HH_MM.test(value)) return 'time'
  // §9.434 - a closed set of three, so a text box would let a fourth word through and the server
  // would narrow it back to the card without saying so.
  if (key === 'trigger') return 'choice'
  return 'text'
}

/**
 * The fields a campaign offers, in the order its settings are stored.
 *
 * <p>Stored order rather than alphabetical: the backend writes them in the order the catalogue
 * declares, which groups related knobs together — `startTime` beside `endTime` — and sorting would
 * scatter them.
 */
/**
 * Settings this editor cannot draw, named rather than dropped - §9.310, owner O9, FB-486.
 *
 * `campaignFields` keeps scalars and silently discards anything structured, which is right for the
 * inputs it renders and wrong as the whole story: `slots` is the mini-game **prize table** that
 * `MiniGameService.draw` picks a winner from, and `segments` is who a personalised offer targets.
 * Both are stored, both are honoured, and neither appeared anywhere on the screen.
 *
 * **A screen that shows four of a campaign's six settings is worse than one that shows four and says
 * there are six.** That is the rule `isKnownField` already applies to a key whose meaning this build
 * does not know; this applies it to a value whose shape it cannot edit.
 */
export function unshownSettings(settings: unknown): string[] {
  if (typeof settings !== 'object' || settings === null || Array.isArray(settings)) return []
  return Object.entries(settings as Record<string, unknown>)
    .filter(([, value]) => value !== null && typeof value === 'object')
    .map(([key]) => key)
}

/** What to tell the operator about them. Empty when there are none, which is most campaigns. */
export function unshownSettingsNote(settings: unknown): string {
  const keys = unshownSettings(settings)
  if (keys.length === 0) return ''
  return `${tt('Táto kampaň má aj nastavenia, ktoré sa tu zatiaľ nedajú upraviť')}: ${keys.join(', ')}`
}

/**
 * The channels a campaign may send on - §9.310.
 *
 * **The server has read this since the port and nothing could write it.** `CampaignRunner.channelOn`
 * looks for `settings.channels` and, finding none, answers *e-mail on, push on, SMS off* for every
 * campaign. None of the fifteen carries the key, so that fallback has always been the whole
 * behaviour and the owner has never been able to change it.
 *
 * The defaults here are the server's own fallback, so drawing the switches on a campaign that has no
 * `channels` shows what it is actually doing rather than a guess.
 */
export const CAMPAIGN_CHANNELS: readonly string[] = ['email', 'push', 'sms']

export interface CampaignChannels {
  email: boolean
  push: boolean
  sms: boolean
}

export function channelsOf(settings: unknown): CampaignChannels {
  const stored =
    typeof settings === 'object' && settings !== null && !Array.isArray(settings)
      ? (settings as Record<string, unknown>).channels
      : undefined
  const map =
    typeof stored === 'object' && stored !== null && !Array.isArray(stored)
      ? (stored as Record<string, unknown>)
      : {}
  // `channelOn`'s rule, restated so the screen and the runner cannot drift: e-mail and push are on
  // unless explicitly false, SMS is off unless explicitly true.
  return {
    email: map.email !== false,
    push: map.push !== false,
    sms: map.sms === true,
  }
}

// ─── §9.777 - what a campaign says, and how ──────────────────────────────────

/** The texts each channel carries. */
export function messageTextKeys(channel: MessageChannel): MessageTextKey[] {
  if (channel === 'push') return ['pushTitle', 'pushBody']
  if (channel === 'email') return ['emailSubject', 'emailHeadline', 'emailMessage']
  return ['smsText']
}

export function messageTextLabel(key: MessageTextKey): string {
  switch (key) {
    case 'pushTitle':
      return tt('Nadpis push správy')
    case 'pushBody':
      return tt('Text push správy')
    case 'emailSubject':
      return tt('Predmet e-mailu')
    case 'emailHeadline':
      return tt('Nadpis v e-maile')
    case 'emailMessage':
      return tt('Text e-mailu')
    case 'smsText':
      return tt('Text SMS')
  }
}

/** The mode a card stores, read as the server reads it: anything but `all` is the default. */
export function channelModeOf(settings: unknown): ChannelMode {
  const stored =
    typeof settings === 'object' && settings !== null && !Array.isArray(settings)
      ? (settings as Record<string, unknown>).channelMode
      : undefined
  return stored === 'all' ? 'all' : 'fallback'
}

export function channelModeLabel(mode: ChannelMode): string {
  return mode === 'all'
    ? tt('Všetkými zapnutými kanálmi')
    : tt('Prvý, ktorý doručí (push, potom e-mail, potom SMS)')
}

/**
 * The text the card shows for one key: the campaign's own when it says something, else what would
 * be sent - the server's resolved default. The runner's rule (`CampaignTexts.text`).
 */
export function messageText(campaign: Campaign, key: MessageTextKey): string {
  const stored = campaign.settings[key]
  if (typeof stored === 'string' && stored.trim() !== '') return stored
  return campaign.messages?.defaults[key] ?? ''
}

/**
 * §9.780 - the text a send by hand would say for one key: the card's own `manual*` text when it says
 * something, else the server's by-hand default.
 */
export function byHandText(campaign: Campaign, key: MessageTextKey): string {
  const stored = campaign.settings[byHandKeyOf(key)]
  if (typeof stored === 'string' && stored.trim() !== '') return stored
  return campaign.messages?.byHandDefaults[key] ?? ''
}

/** §9.780 - whether the card draws *Pri ručnom poslaní*: the server answers by-hand texts for it. */
export function hasByHandTexts(messages: CampaignMessages | null): boolean {
  return messages !== null && Object.keys(messages.byHandDefaults).length > 0
}

/**
 * The texts and mode to PATCH - only what moved.
 *
 * **A text typed back to the default is saved blank**, which the server reads as *the default*, so a
 * campaign keeps following its default rather than freezing a copy of it - and that is also how
 * *Pôvodný text* works. §9.780 - the texts of a send by hand the same way, under their `manual*` keys.
 */
export function changedMessages(
  campaign: Campaign,
  texts: Readonly<Partial<Record<MessageTextKey, string>>>,
  mode: ChannelMode | undefined,
  byHand: Readonly<Partial<Record<MessageTextKey, string>>> = {},
): Record<string, string> {
  const changed: Record<string, string> = {}
  for (const key of MESSAGE_TEXT_KEYS) {
    const typed = texts[key]
    if (typed === undefined || typed === messageText(campaign, key)) continue
    changed[key] = typed === (campaign.messages?.defaults[key] ?? '') ? '' : typed
  }
  for (const key of MESSAGE_TEXT_KEYS) {
    const typed = byHand[key]
    if (typed === undefined || typed === byHandText(campaign, key)) continue
    changed[byHandKeyOf(key)] = typed === (campaign.messages?.byHandDefaults[key] ?? '') ? '' : typed
  }
  if (mode !== undefined && mode !== channelModeOf(campaign.settings)) changed.channelMode = mode
  return changed
}

/** `{meno}, {kupón}` - what a campaign fills, as the card lists it. */
export function placeholderList(placeholders: readonly string[]): string {
  return placeholders.map((name) => `{${name}}`).join(', ')
}

/**
 * How many SMS a text takes: 160 characters for one, 153 a part beyond that. Counted after the
 * diacritics are dropped, because the gateway transliterates before sending (`SmsGateway`); a
 * placeholder's value changes the length, which the card says.
 */
export function smsSegments(text: string): number {
  const plain = text.normalize('NFD').replace(/[\u0300-\u036f]/gu, '')
  if (plain.length === 0) return 0
  return plain.length <= 160 ? 1 : Math.ceil(plain.length / 153)
}

export function campaignFieldLabelForChannel(channel: string): string {
  if (channel === 'email') return tt('E-mail')
  if (channel === 'push') return tt('Push notifikácia')
  return tt('SMS')
}

/**
 * @param type the campaign's own type, so a setting it supports can be offered before it is stored -
 *   §9.434. Optional, and every caller that predates it keeps its exact behaviour.
 *
 *   **`kuponove_tornado` needs this.** Its `trigger` is the setting the owner asked for, and a
 *   franchise that has never chosen one has no such key - so a list built from the stored object
 *   alone would show no control and the choice would be unreachable. It is injected with the value
 *   the server defaults to, so the card shows what is in force rather than a blank.
 */
export function campaignFields(settings: unknown, type?: string): CampaignField[] {
  if (typeof settings !== 'object' || settings === null || Array.isArray(settings)) {
    return type === 'kuponove_tornado'
      ? [{ key: 'trigger', kind: 'choice', value: tornadoTrigger(null) }]
      : []
  }
  const stored = settings as Record<string, unknown>
  const fields = Object.entries(stored).flatMap((entry) => {
    const [key, value] = entry
    if (value === null || typeof value === 'object') return []
    return [{ key, kind: campaignFieldKind(key, value), value: value as string | number | boolean }]
  })
  if (type === 'kuponove_tornado' && !fields.some((field) => field.key === 'trigger')) {
    return [{ key: 'trigger', kind: 'choice', value: tornadoTrigger(stored) }, ...fields]
  }
  return fields
}

/**
 * What to PATCH: only the keys somebody actually changed.
 *
 * <p><b>The server merges settings rather than replacing them</b>, deliberately — a campaign whose
 * wording was customised must not lose it because a discount was toggled. Sending the whole object
 * back would overwrite keys this screen never showed, which is the exact loss the merge prevents.
 */
export function changedSettings(
  original: unknown,
  edited: Readonly<Record<string, string | number | boolean>>,
  /**
   * The channels, when the operator touched them - §9.310.
   *
   * **Passed separately because it is the one setting this editor can *create*.** Everything else
   * here is an edit to a key the campaign already had; `channels` is stored by none of the fifteen,
   * so a loop over what exists could never produce it. Omitted when unchanged, so a save that did not
   * touch the switches does not start writing a key that was not there.
   */
  channels?: CampaignChannels,
  /**
   * The campaign's type - §9.436, the owner's report.
   *
   * **`trigger` is the second setting this editor can create, and the note above predicted it.**
   * That note says `channels` is *"the one setting this editor can create… a loop over what exists
   * could never produce it"* - and §9.434 added `trigger`, which a franchise that has never chosen
   * one does not have. So the loop below could not see it: the owner changed *Kedy sa kampaň spustí*
   * to *po objednávke z webu*, the save succeeded, and the key was never in the payload.
   *
   * Passing the type here rather than special-casing `trigger` beside `channels`, because
   * `campaignFields` already knows which settings a type supports and this is the same question.
   */
  type?: string,
  /**
   * The mini-game's prize slots, when the operator touched them - §9.481.
   *
   * **The third setting this editor can create**, after `channels` and `trigger`, and separate for the same
   * reason they are: `campaignFields` drops any value that is an object, so a loop over what the scalar
   * editor knows about could never produce a list. Only the two `MINI_GAME_TYPES` pass one.
   */
  slots?: SlotForm,
): Record<string, unknown> {
  const before = campaignFields(original, type)
  const changed: Record<string, unknown> = {}
  for (const field of before) {
    const next = edited[field.key]
    if (next === undefined) continue
    if (next !== field.value) changed[field.key] = next
  }
  if (channels !== undefined) {
    const stored = channelsOf(original)
    const moved =
      channels.email !== stored.email ||
      channels.push !== stored.push ||
      channels.sms !== stored.sms
    // Written whole rather than key by key: the server merges the *settings* object, not the map
    // inside it, so a partial `channels` would drop the two the operator did not touch.
    if (moved) changed.channels = { ...channels }
  }
  /* §9.481 - **`slots` is the third setting this editor can create**, and the note on `channels` above
     predicted this pattern for the second. Written **whole** for that same reason: the server merges the
     settings object and not the list inside it, so a partial send would replace the whole list with a
     fragment. Omitted when nothing moved, so a save that only toggled a discount does not start writing
     the prize slots. */
  if (slots !== undefined && slotsChanged(original, slots)) {
    changed.slots = slotsPayload(slots)
  }
  return changed
}

/** A number field parsed back from its input, refusing what a settings value cannot be. */
export function parseFieldValue(
  kind: CampaignFieldKind,
  raw: string,
): string | number | boolean | null {
  if (kind === 'number') {
    // `Number('')` is 0, not NaN — so a cleared field would save a silent zero, and a cashback of
    // 0 % is a campaign that gives nothing while looking configured. Blank is refused.
    if (raw.trim() === '') return null
    const parsed = Number(raw)
    // Not `NaN`, and not negative: every numeric setting here is a percentage, a count or a money
    // floor, and none of them is meaningful below zero.
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null
  }
  if (kind === 'time') return HH_MM.test(raw) ? raw : null
  if (kind === 'boolean') return raw === 'true'
  return raw
}

// ─── §9.160 — editing the wheel ──────────────────────────────────────────────
//
// The card could always show the wheel and never change it: no switch, no editor, `is_active` false
// in the data and no way to make it true. The server has always taken all four fields.

export interface WheelForm {
  minOrder: string
  rewardValidityHours: string
  segments: Array<{ label: string; weight: string; color: string; type: string; value: string }>
}

/** The stored wheel as a form fills it — money and counts as the text somebody types. */
export function wheelFormOf(wheel: Wheel): WheelForm {
  return {
    // A comma, because this lands in an input a Slovak owner reads and types into.
    // `wheelPayload` turns it back before it goes on the wire.
    minOrder: formatDecimal(wheel.minOrderCents / 100, 2),
    rewardValidityHours: String(wheel.rewardValidityHours),
    segments: wheel.segments.map((segment) => ({
      label: segment.label,
      weight: String(segment.weight),
      color: segment.color,
      type: segment.type,
      value: String(segment.value),
    })),
  }
}

export type WheelProblem =
  | { kind: 'no-segments' }
  | { kind: 'no-weight' }
  | { kind: 'bad-number'; field: string }
  | { kind: 'empty-label' }
  /** §9.478 - a winning segment worth nothing, named by its label so the operator finds it. */
  | { kind: 'worthless-segment'; label: string }
  /** §9.478 - a losing segment carrying a value, which would be a lie either way. */
  | { kind: 'loser-with-value'; label: string }
  /** §9.478 - a discount over a hundred per cent, which is the shop paying the customer. */
  | { kind: 'over-full-discount'; label: string }

/**
 * What stops the wheel being saved.
 *
 * <p><b>Every weight at zero is the one that matters.</b> A spin picks a segment in proportion to its
 * weight; with nothing to divide by there is no answer, and a wheel that cannot choose is worse than
 * one that is switched off — it is switched on and broken. The server validates its own copy, but a
 * customer spinning is not the moment to find out.
 */
export function wheelProblems(form: WheelForm): WheelProblem[] {
  const problems: WheelProblem[] = []
  if (form.segments.length === 0) problems.push({ kind: 'no-segments' })
  if (form.segments.some((segment) => segment.label.trim() === '')) {
    problems.push({ kind: 'empty-label' })
  }
  for (const [field, raw, floor] of [
    ['minOrder', form.minOrder, 0],
    /* §9.478 - **one, not nought.** `WheelService` floors this field at 1, and this read `< 0`, so a
       blank box (`Number('')` is **0**, which is finite and not negative) passed the screen and the
       server refused it - the operator got a toast instead of a message at the field. §3.25's shape. */
    ['rewardValidityHours', form.rewardValidityHours, 1],
  ] as const) {
    /* **The emptiness check is its own condition.** No sign or range test can express *nothing was
       typed*, because `Number('')` is 0 - which is the whole lesson of the sweep that found §9.478. */
    const value = raw.trim() === '' ? Number.NaN : Number(raw.replace(',', '.'))
    if (!Number.isFinite(value) || value < floor) problems.push({ kind: 'bad-number', field })
  }
  /* §9.478 - **what a segment is worth, which this function never asked.** It checks the label for
     emptiness and the weight for a sign, and `wheelPayload` then sends
     `Number(segment.value.replace(',', '.'))` - so a cleared box becomes 0 and the wheel advertises a
     prize worth nothing. The server did not check it either (§10A.240), and the spin path's
     `value > 0 ? … : null` turned it into a quiet disappointment.

     Per type, because `no_win` is the point of having one: a blanket *every value is positive* would
     make a wheel with a loser unsaveable, which is §3.14's mistake. */
  for (const segment of form.segments) {
    const label = segment.label.trim()
    /* **No separate emptiness check here, and a mutation is why.** Removing one left every case green:
       a blank gives `Number('') === 0` and the `<= 0` below already refuses it, so the extra condition
       was one no case could exercise - which is the thing this session keeps calling out.
       It *is* load-bearing on `minOrder` and `rewardValidityHours` above, whose floors are 0 and 1: a
       blank giving 0 passes a floor of 0, so there it has to be asked separately. Same trap, opposite
       answer, and the difference is the floor. */
    const worth = Number(segment.value.trim().replace(',', '.'))
    if (segment.type === 'no_win') {
      if (Number.isFinite(worth) && worth !== 0) {
        problems.push({ kind: 'loser-with-value', label })
      }
      continue
    }
    if (!Number.isFinite(worth) || worth <= 0) {
      problems.push({ kind: 'worthless-segment', label })
      continue
    }
    // Only `percent` has a ceiling: a fixed prize of 120 EUR is generosity, not an error.
    if (segment.type === 'percent' && worth > 100) {
      problems.push({ kind: 'over-full-discount', label })
    }
  }
  const weights = form.segments.map((segment) => Number(segment.weight))
  if (weights.some((weight) => !Number.isFinite(weight) || weight < 0)) {
    problems.push({ kind: 'bad-number', field: 'weight' })
  } else if (form.segments.length > 0 && weights.reduce((sum, weight) => sum + weight, 0) === 0) {
    problems.push({ kind: 'no-weight' })
  }
  return problems
}

export function wheelProblemMessage(problem: WheelProblem): string {
  if (problem.kind === 'no-segments') return tt('Koleso potrebuje aspoň jedno políčko.')
  if (problem.kind === 'empty-label')
    return tt('Každé políčko potrebuje názov - zákazník ho uvidí.')
  if (problem.kind === 'no-weight') {
    return tt('Aspoň jedno políčko musí mať váhu väčšiu ako 0, inak sa koleso nemá ako rozhodnúť.')
  }
  /* §9.478 - **each names the segment**, because a wheel has up to twenty and *a value is wrong* would
     send the operator through all of them. The label is what they see in the editor and what the
     customer sees on the wheel, so it is the one identifier both ends share. */
  if (problem.kind === 'worthless-segment') {
    return `${problem.label}: ${tt('zadajte hodnotu vyššiu než nula - inak políčko sľúbi výhru a nedá nič.')}`
  }
  if (problem.kind === 'loser-with-value') {
    return `${problem.label}: ${tt('políčko bez výhry nemá mať hodnotu.')}`
  }
  if (problem.kind === 'over-full-discount') {
    return `${problem.label}: ${tt('zľava nemôže byť vyššia než 100 %.')}`
  }
  return tt('Zadajte platné číslo.')
}

/** What the form sends. Money as the decimal string the API takes, counts as numbers. */
export function wheelPayload(form: WheelForm): Record<string, unknown> {
  return {
    // A wire format, not a screen: the API takes a decimal string with a dot, and `formatDecimal`
    // would send it a comma.
    // eslint-disable-next-line no-restricted-syntax
    minOrderAmount: Number(form.minOrder.replace(',', '.')).toFixed(2),
    rewardValidityHours: Math.round(Number(form.rewardValidityHours)),
    segments: form.segments.map((segment) => ({
      type: segment.type,
      label: segment.label.trim(),
      value: Number(segment.value.replace(',', '.')),
      weight: Number(segment.weight),
      color: segment.color,
    })),
  }
}

/** The odds a set of weights produces, so the editor can show them while they are typed. */
export function weightOdds(form: WheelForm): number[] {
  const weights = form.segments.map((segment) => Math.max(0, Number(segment.weight) || 0))
  const total = weights.reduce((sum, weight) => sum + weight, 0)
  return total === 0 ? weights.map(() => 0) : weights.map((weight) => (weight / total) * 100)
}

// ─── §9.339 - the guard in front of the SMS switch ───────────────────────────
//
// `BACKEND.md` §10A.116 moved the guard from the send to the switch, and this is why: *"that is the
// irreversible decision - after it, every trigger costs money quietly."* The owner's terms for the
// whole feature were *"a recipient count and a cost shown before the button, and a typed
// confirmation, the way the manual SMS sender already works."*
//
// Turning it **off** is not guarded, deliberately: a confirmation on the corrective action is a toll
// on fixing a mistake. The same asymmetry `SAAS.md` §3.3's *Všetky / Žiadne* has.

/** 2,6 cents a segment - the price this product already quotes, shared with the manual sender. */
export const SMS_SEGMENT_CENTS = 2.6

/**
 * What turning it on will cost, per trigger.
 *
 * **Per trigger and not per blast**, because that is what a campaign is: §10A.116 chose the shape
 * over a blast button precisely so one click cannot text everybody. The figure quoted is therefore
 * the campaign's own reach - the worst case for *this* campaign, which for
 * `reaktivacia_neaktivnych` can be hundreds in a day.
 */
export function smsRunCostCents(recipients: number): number {
  return Math.round(Math.max(0, recipients) * SMS_SEGMENT_CENTS)
}

/**
 * The sentence in front of the switch.
 *
 * Both numbers, because either alone is misleading: a count with no price does not say it costs
 * anything, and a price with no count cannot be checked against what the campaign matches.
 */
export function smsSwitchWarning(recipients: number): string {
  return `${tt('Zapnutím SMS začne táto kampaň pri každom spustení posielať SMS až')} ${formatCount(recipients)} ${tt('zákazníkom - približne')} ${formatCents(smsRunCostCents(recipients))} ${tt('za jedno spustenie.')}`
}

/**
 * The phrase somebody has to type, and it names the channel rather than being the word *yes*.
 *
 * The `anonymisePhrase` / `anonymiseConfirmed` pair on `/marketing-customers` is the precedent, and
 * it is re-used rather than re-invented: one confirmation idiom on this product, compared the same
 * way. Case- and space-insensitively, because legacy compared with `trim()` alone and refused a
 * lowercase answer without saying why.
 */
export function smsConfirmPhrase(): string {
  return 'ZAPNUT SMS'
}

export function smsConfirmed(typed: string): boolean {
  return typed.trim().replace(/\s+/gu, ' ').toUpperCase() === smsConfirmPhrase()
}
